import { useCallback, useRef, useState } from "react";
import { errorHandler } from "../APIs/errorHandler";
import {
  fetchOrganisationById,
  fetchOrganisations,
  OrganisationOption
} from "../APIs/Organisation/GetOrganisations";
import { fetchVenueGroupConfig } from "../APIs/Venue/GetVenueGroupConfig";
import AccessDeniedContent from "../components/AccessDeniedContent";
import OrganisationSelectionContent from "../components/OrganisationSelectionContent";
import { useInfoModal } from "../context/InfoModalContext";
import { useLoadingStatus } from "../context/LoadingStatusContext";
import { dmnIdentity } from "../utils/dmnIdentity";
import { useAppContext } from "./useAppContext";

const useManageOrgData = () => {
  const { setVenueGroupConfig, setVenueUserConfig } = useAppContext();
  const { showInfoModal, hideInfoModal } = useInfoModal();
  const { updateStage } = useLoadingStatus();
  const [isLoading, setIsLoading] = useState(false);
  const loadingRef = useRef(false);

  const handleOrganisationSelect = useCallback(
    async (organisationId: string) => {
      hideInfoModal();
      setIsLoading(true);
      updateStage("organisation", "loading");

      try {
        const organisationConfig = await fetchOrganisationById(organisationId);

        if (organisationConfig?.venueGroup?._id) {
          setVenueGroupConfig(organisationConfig.venueGroup);
          updateStage("organisation", "success");
        } else {
          updateStage("organisation", "error", {
            message: "Failed to load organisation configuration.",
            technicalDetails:
              "fetchOrganisationById returned null or missing venueGroup._id"
          });
          noOrganisationsAvailable();
          errorHandler(
            new Error("Failed to fetch organisation configuration"),
            "Error loading organisation"
          );
        }
      } catch (error) {
        updateStage("organisation", "error", {
          message: "Error loading organisation configuration.",
          technicalDetails:
            error instanceof Error ? error.message : String(error)
        });
        errorHandler(error, "Error loading organisation");
      } finally {
        setIsLoading(false);
      }
    },
    [hideInfoModal, setVenueGroupConfig, updateStage]
  );

  const showOrganisationSelectionModal = useCallback(
    (organisations: OrganisationOption[]) => {
      showInfoModal({
        title: "Select Organisation",
        children: (
          <OrganisationSelectionContent
            organisations={organisations}
            onSelect={handleOrganisationSelect}
          />
        )
      });
    },
    [showInfoModal, handleOrganisationSelect]
  );

  const noOrganisationsAvailable = useCallback(() => {
    showInfoModal({
      title: "Access Denied",
      children: <AccessDeniedContent />,
      actions: [
        {
          name: "signOut",
          type: "primary",
          text: "OK"
        }
      ],
      onAction: (actionName) => {
        if (actionName === "signOut") {
          dmnIdentity.signOut();
        }
      }
    });
  }, [showInfoModal]);

  const fetchVenueGroupData = useCallback(async () => {
    try {
      const venueGroupConfig = await fetchVenueGroupConfig();
      return { venueGroupConfig, error: null };
    } catch (error) {
      const wrappedError =
        error instanceof Error ? error : new Error(String(error));
      return { venueGroupConfig: undefined, error: wrappedError };
    }
  }, []);

  const fetchOrganisationList = useCallback(async () => {
    try {
      const organisations = await fetchOrganisations();
      return { organisations, error: null };
    } catch (error) {
      const wrappedError =
        error instanceof Error ? error : new Error(String(error));
      return { organisations: undefined, error: wrappedError };
    }
  }, []);

  const handleOrganisationResult = useCallback(
    async (
      organisations: OrganisationOption[] | undefined,
      organisationsError: Error | null,
      venueGroupConfigError: Error | null
    ) => {
      if (venueGroupConfigError && organisationsError) {
        const combinedDetails = [
          "Both venue group APIs failed:",
          "",
          "1. GET /venues_group:",
          venueGroupConfigError.message,
          "",
          "2. GET /venue-groups:",
          organisationsError.message
        ].join("\n");

        updateStage("organisation", "error", {
          message:
            "Failed to load organisation data from both APIs. Please try again.",
          technicalDetails: combinedDetails
        });
        errorHandler(organisationsError, "Error loading organisation data");
        noOrganisationsAvailable();
        return;
      }

      if (organisationsError) {
        updateStage("organisation", "error", {
          message: "Failed to load organisation list. Please try again.",
          technicalDetails: organisationsError.message
        });
        errorHandler(organisationsError, "Error loading organisation data");
        noOrganisationsAvailable();
        return;
      }

      if (!organisations || organisations.length === 0) {
        const details = venueGroupConfigError
          ? `GET /venues_group: ${venueGroupConfigError.message}\n\nGET /venue-groups: returned empty array`
          : "fetchOrganisations() returned empty array. User may not have allowed_orgs configured.";

        updateStage("organisation", "error", {
          message: "You don't have permission to access any organisations.",
          technicalDetails: details
        });
        noOrganisationsAvailable();
        return;
      }

      if (organisations.length === 1) {
        await handleOrganisationSelect(organisations[0]._id);
        return;
      }

      updateStage("organisation", "success");
      showOrganisationSelectionModal(organisations);
    },
    [
      updateStage,
      handleOrganisationSelect,
      showOrganisationSelectionModal,
      noOrganisationsAvailable
    ]
  );

  /**
   * Loads organisation data by attempting to fetch from two venue group APIs.
   * First tries /venues_group endpoint. If that returns no venue group, falls back to /venue-groups.
   */
  const loadOrganisationData = useCallback(async () => {
    if (loadingRef.current) {
      return;
    }

    loadingRef.current = true;
    setIsLoading(true);
    updateStage("organisation", "loading");

    const result = await fetchVenueGroupData();

    if (result.venueGroupConfig?.user) {
      setVenueUserConfig(result.venueGroupConfig.user);
    }

    if (result.venueGroupConfig?.venueGroup?._id) {
      setVenueGroupConfig(result.venueGroupConfig.venueGroup);
      updateStage("organisation", "success");
      loadingRef.current = false;
      setIsLoading(false);
      return result.venueGroupConfig;
    }

    const orgResult = await fetchOrganisationList();

    await handleOrganisationResult(
      orgResult.organisations,
      orgResult.error,
      result.error
    );

    loadingRef.current = false;
    setIsLoading(false);
  }, [
    setVenueGroupConfig,
    setVenueUserConfig,
    updateStage,
    fetchVenueGroupData,
    fetchOrganisationList,
    handleOrganisationResult
  ]);

  return {
    loadOrganisationData,
    isLoading
  };
};

export default useManageOrgData;
